import { getFullName } from "utils/Haim/utils";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { formatPhoneLocal } from "../../../services/signatureFormatters";
import type { ContactSummary } from "../../../hooks/contacts/contactSummary";

// Single source of truth for "given a phone, what name + phone do I show?"
// Used by every contact-widget render path (bulk messaging log table/cards,
// signatures sent list table/cards, signature detail panel) so a single
// realtor sees the same name everywhere — and when they update a contact's
// name in the CRM, every screen reflects it without code changes.
//
// Lookup order for the displayed name:
//   1. The looked-up contact's `callerFirstName` + `callerLastName`
//      (the canonical pair the contacts page edits).
//   2. The looked-up contact's `contactFullName` — set by a server-side
//      function for contacts that came in via import / chrome extension
//      and don't have the first/last split. Without this fallback, a
//      lot of imported contacts would render as bare phone numbers.
//   3. The caller-supplied `nameFallback` — typically a snapshot name
//      from the document/signer at the time it was sent, used when
//      the recipient isn't in the realtor's contacts at all.
//   4. Empty string — the consumer can then choose to show the phone
//      in the primary slot.
//
// Where the contact comes from: a LOOKUP FUNCTION, the `lookup` that
// `useContactsLookup` returns — not the realtor's whole book as an array.
// The screen calls that hook ONCE with every phone it is showing and hands
// `lookup` in here for each row. That keeps this a pure function with no
// hook inside it: it can run in a `useMemo` or a `.map()`, where a hook per
// row would break the rules of hooks, and a test can pass any function.
//
// `lookup` has three answers and two of them mean "no CRM name":
// `undefined` (not answered yet, or the request failed) and `null`
// (answered: not in the book). They display identically here, so a row is
// readable before its answer lands and gains the CRM name when it does.
// Nothing in the result says which of the two it was — a caller that needs
// to tell them apart (to offer "add to contacts") asks `lookup` itself.

/** The fields a display name is built from. A `ContactSummary` satisfies it. */
type ContactNameFields = Pick<
  ContactSummary,
  "callerFirstName" | "callerLastName" | "contactFullName"
>;

/** `useContactsLookup().lookup`, as far as this file reads its answer. */
export type ContactNameLookup = (
  phone: string | null | undefined
) => ContactNameFields | null | undefined;

// No `contact` on the result, on purpose: what a lookup returns is a
// projection of the contact, and a field named `contact` gets handed to
// code that expects the whole document.
export type ContactDisplay = {
  /** Best name we have to show in the primary slot. Empty string when
   *  there's nothing — callers usually fall back to `displayPhone` then. */
  contactName: string;
  /** Phone in local national format with dashes (`052-748-5568`). */
  displayPhone: string;
  /** Two-letter country code from libphonenumber, used to render the
   *  flag emoji/icon next to international numbers. Undefined when the
   *  number is unparseable. */
  country: string | undefined;
};

const EMPTY: ContactDisplay = {
  contactName: "",
  displayPhone: "",
  country: undefined
};

export function computeContactDisplay(
  phone: string | undefined | null,
  lookup: ContactNameLookup,
  nameFallback?: string
): ContactDisplay {
  if (!phone) {
    return nameFallback ? { ...EMPTY, contactName: nameFallback } : EMPTY;
  }

  // `lookup` takes the phone in whatever spelling the screen holds it and
  // resolves it to the contact id itself, so there is nothing to normalise
  // and no list to search here.
  const contact = lookup(phone);

  const fromContact = contact
    ? getFullName(contact.callerFirstName, contact.callerLastName) ||
      contact.contactFullName ||
      ""
    : "";

  const contactName = fromContact || nameFallback || "";

  let displayPhone = "";
  let country: string | undefined;
  try {
    const parsed = parsePhoneNumber(phone);
    displayPhone = parsed?.formatNational()?.replace(/ /g, "-") ?? phone;
    country = parsed?.country;
  } catch {
    displayPhone = phone;
  }
  // Fallback when libphonenumber rejected the number — still show
  // *something* readable rather than blanking out.
  if (!displayPhone) displayPhone = formatPhoneLocal(phone) || phone;

  return { contactName, displayPhone, country };
}
